import type React from 'react';

import { useEffect, useState } from 'react';
import { notification, Row, Input, Card, Col } from 'antd';
import { useQuery } from 'react-query';
import { SearchOutlined, HolderOutlined } from '@ant-design/icons';
import {
  addMgProduct,
  type Mgproduct,
  getMgByPrcode,
  removeMgProduct,
} from '../../../../services/parametersService';
import { useGetManagementCompanies } from 'hooks/useGetManagementCompanies';
import moment from 'moment';
import TitlePage from 'components/theme_components/Title';
import Title from 'components/Title';
import Notifications from 'components/theme_components/Notifications';
import GetLabel from 'DisplayFramework/components/GetLabel';
import { useNavigate } from 'react-router-dom';

type ProductFamilyMGTransferProps = {
  prcode: string;
};

interface MgItem {
  mgid: number;
  mgname: string;
}

const ProductFamilyMGTransfer = (props: ProductFamilyMGTransferProps) => {
  const { data: mgList = [] } = useGetManagementCompanies();
  const [availableItems, setAvailableItems] = useState<MgItem[]>([]);
  const [associatedItems, setAssociatedItems] = useState<MgItem[]>([]);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchAssociated, setSearchAssociated] = useState('');
  const [draggedItem, setDraggedItem] = useState<MgItem | null>(null);
  const [dragOverZone, setDragOverZone] = useState<string | null>(null);

  const { prcode } = props;
  const navigate = useNavigate();
  /* const { data: mgAssociatedList = [], refetch } = useQuery(
    ['getMgByPrcode', prcode],
    () =>
      getMgByPrcode({ prcode })
        .then((res) => res.list || [])
        .catch(() => [])
  );*/

  const { data: mgAssociatedList = [], refetch } = useQuery(
    ['getMgByPrcode', prcode],
    () =>
      getMgByPrcode({ prcode })
        .then((res) => res.list || [])
        .catch(() => []),
    {
      enabled: !!prcode,
    }
  );

  // Update lists when data changes
  /*useEffect(() => {
    if (mgList && mgAssociatedList) {
      const associatedIds = mgAssociatedList.map((mg) => String(mg.mgid));

      const associated = mgList.filter((mg) =>
        associatedIds.includes(String(mg.mgid))
      );
      const available = mgList.filter(
        (mg) => !associatedIds.includes(String(mg.mgid))
      );

      setAssociatedItems(associated);
      setAvailableItems(available);
    }
  }, [mgList, mgAssociatedList]);*/
  useEffect(() => {
    if (!mgList || !mgAssociatedList) return;

    const associatedIds = mgAssociatedList.map((mg) => String(mg.mgid));
    const associated = mgList.filter((mg) =>
      associatedIds.includes(String(mg.mgid))
    );
    const available = mgList.filter(
      (mg) => !associatedIds.includes(String(mg.mgid))
    );

    // Vérifie si le contenu a vraiment changé avant de faire setState
    const hasChanged = (a: MgItem[], b: MgItem[]) =>
      JSON.stringify(a) !== JSON.stringify(b);

    if (hasChanged(associatedItems, associated)) {
      setAssociatedItems(associated);
    }
    if (hasChanged(availableItems, available)) {
      setAvailableItems(available);
    }
  }, [mgList, mgAssociatedList]);

  const handleDragStart = (e: React.DragEvent, item: MgItem) => {
    setDraggedItem(item);
    e.dataTransfer.setData('text/plain', item.mgid.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverZone(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, zone: string) => {
    e.preventDefault();
    setDragOverZone(zone);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear drag over zone if we're leaving the drop zone entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverZone(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetZone: string) => {
    e.preventDefault();
    setDragOverZone(null);

    if (!draggedItem) return;

    const sourceZone = availableItems.some(
      (item) => item.mgid === draggedItem.mgid
    )
      ? 'available'
      : 'associated';

    if (sourceZone === targetZone) return;

    const mgid = draggedItem.mgid;

    if (sourceZone === 'available' && targetZone === 'associated') {
      // Moving from available to associated
      setAvailableItems((prev) => prev.filter((item) => item.mgid !== mgid));
      setAssociatedItems((prev) => [...prev, draggedItem]);

      const mgProductrequest: Mgproduct = {
        enddate: '',
        mgid,
        prcode,
        startdate: moment(Date.now()).format('YYYY-MM-DD'),
      };

      try {
        const response = await addMgProduct({ mgProductrequest });
        if (response.code === '200') {
          Notifications({
            status: 'success',
            message: response.message || 'Successfully added',
            description: response.description,
            duration: 2,
            placement: 'top',
          });
          refetch();
          navigate('/configuration-sfs');
        } else {
          Notifications({
            status: 'error',
            message: response.message || 'Failed to add',
            description: response.description,
            duration: 2,
            placement: 'top',
          });
          // Revert on failure
          setAvailableItems((prev) => [...prev, draggedItem]);
          setAssociatedItems((prev) =>
            prev.filter((item) => item.mgid !== mgid)
          );
        }
      } catch (error) {
        notification.error({
          message: 'An error occurred',
        });
        // Revert on error
        setAvailableItems((prev) => [...prev, draggedItem]);
        setAssociatedItems((prev) => prev.filter((item) => item.mgid !== mgid));
      }
    } else if (sourceZone === 'associated' && targetZone === 'available') {
      // Moving from associated to available
      setAssociatedItems((prev) => prev.filter((item) => item.mgid !== mgid));
      setAvailableItems((prev) => [...prev, draggedItem]);

      try {
        const response = await removeMgProduct({ mgid, prcode });
        if (response.code === '200') {
          Notifications({
            status: 'success',
            message: response.message || 'Successfully removed',
            description: response.description,
            duration: 2,
            placement: 'top',
          });
          refetch();
          navigate('/configuration-sfs');
        } else {
          Notifications({
            status: 'error',
            message: response.message || 'Failed to remove',
            description: response.description,
            duration: 2,
            placement: 'top',
          });
          // Revert on failure
          setAssociatedItems((prev) => [...prev, draggedItem]);
          setAvailableItems((prev) =>
            prev.filter((item) => item.mgid !== mgid)
          );
        }
      } catch (error) {
        notification.error({
          message: 'An error occurred',
        });
        // Revert on error
        setAssociatedItems((prev) => [...prev, draggedItem]);
        setAvailableItems((prev) => prev.filter((item) => item.mgid !== mgid));
      }
    }

    setDraggedItem(null);
  };

  const filterItems = (items: MgItem[], searchTerm: string) => {
    if (!searchTerm) return items;
    return items.filter((item) =>
      item.mgname.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredAvailable = filterItems(availableItems, searchAvailable);
  const filteredAssociated = filterItems(associatedItems, searchAssociated);

  const itemStyle = (item: MgItem, isDragging = false) => ({
    padding: '8px 12px',
    margin: '4px 0',
    backgroundColor: isDragging ? '#e6f7ff' : 'white',
    border: '1px solid #d9d9d9',
    cursor: 'grab',
    userSelect: 'none' as const,
    display: 'flex-start',
    alignItems: 'center',
    justifyContent: 'space-between',
    opacity: isDragging ? 0.5 : 1,
    boxShadow: isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
    transition: 'all 0.2s ease',
  });

  const dropZoneStyle = (zone: string) => ({
    minHeight: 300,
    maxHeight: 300,
    overflowY: 'auto' as const,
    backgroundColor: dragOverZone === zone ? '#f0f0f0' : 'white',
    border: dragOverZone === zone ? '2px dashed #1890ff' : '1px solid #d9d9d9',
    padding: '8px',
    transition: 'all 0.2s ease',
  });

  const importAll = (r: any) => {
    const images: any = {};
    r.keys().map((item: any, index: any) => {
      images[item.replace('./', '')] = r(item);
    });
    return images;
  };

  const images: any = importAll(
    require.context('/src/assets/', false, /\.(png|jpg|jpe?g|svg)$/)
  );
  return (
    <div className="mt-10 transferSFS">
      <Title
        comcode="infoparproductfamily"
        level={4}
        className="ut-updateTP-title mb-7 mt-10"
      />
      <Row className="mb-2">
        <Col span={11} className="text-center bold">
          <GetLabel label="availableValues" />
        </Col>
        <Col span={2} />
        <Col span={11} className="text-center bold">
          <GetLabel label="associatedValues" />
        </Col>
      </Row>
      <Row gutter={16} className="ratelist">
        <Col xs={24} md={11}>
          <div
            style={dropZoneStyle('available')}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, 'available')}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, 'available')}
            className="listactif"
          >
            {filteredAvailable.map((item) => (
              <div
                key={`available-${item.mgid}`}
                draggable
                onDragStart={(e) => handleDragStart(e, item)}
                onDragEnd={handleDragEnd}
                style={itemStyle(item, draggedItem?.mgid === item.mgid)}
              >
                <HolderOutlined
                  style={{ color: '#999', cursor: 'grab' }}
                  className="mr-2"
                />
                <span>{item.mgname}</span>
              </div>
            ))}
            {filteredAvailable.length === 0 && (
              <div
                style={{
                  padding: '20px',
                  color: '#999',
                  textAlign: 'center',
                }}
              >
                {dragOverZone === 'available'
                  ? 'Drop here to remove from associated'
                  : 'No items available'}
              </div>
            )}
          </div>
        </Col>
        <Col xs={24} md={2} className="image-center-col">
          <div className="image-container">
            <img
              src={images['dragdrop.png'] || '/placeholder.svg'}
              style={{
                width: 70,
                height: 70,
              }}
            />
          </div>
        </Col>
        <Col xs={24} md={11}>
          <div
            style={dropZoneStyle('associated')}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, 'associated')}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, 'associated')}
            className="listactif"
          >
            {filteredAssociated.map((item) => (
              <div
                key={`associated-${item.mgid}`}
                draggable
                onDragStart={(e) => handleDragStart(e, item)}
                onDragEnd={handleDragEnd}
                style={itemStyle(item, draggedItem?.mgid === item.mgid)}
              >
                {' '}
                <HolderOutlined
                  style={{ color: '#999', cursor: 'grab' }}
                  className="mr-2"
                />
                <span>{item.mgname}</span>
              </div>
            ))}
            {filteredAssociated.length === 0 && (
              <div
                style={{
                  padding: '20px',
                  color: '#999',
                  textAlign: 'center',
                }}
              >
                {dragOverZone === 'associated'
                  ? 'Drop here to add to associated'
                  : 'No items associated'}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ProductFamilyMGTransfer;
